'use client';

import { cn } from '@/core/utils/cn';
import { Mail, Phone, Globe } from 'lucide-react';

interface FormFieldProps {
  label: string;
  error?: string;
  hint?: string;
  helpText?: string;
  required?: boolean;
  children: React.ReactNode;
  className?: string;
  maxLength?: number;
  currentLength?: number;
  validationType?: 'email' | 'phone' | 'url';
  id?: string;
}

const validationIcons = {
  email: Mail,
  phone: Phone,
  url: Globe,
};

const validationLabels = {
  email: 'Email format',
  phone: 'Phone format',
  url: 'URL format',
};

export function FormField({
  label,
  error,
  hint,
  helpText,
  required,
  children,
  className,
  maxLength,
  currentLength,
  validationType,
  id,
}: FormFieldProps) {
  const showCharCount = maxLength !== undefined && currentLength !== undefined;
  const charCountExceeded = showCharCount && currentLength > maxLength;
  const ValidationIcon = validationType ? validationIcons[validationType] : null;

  return (
    <div id={id} className={cn('space-y-1.5', className)}>
      <div className="flex items-center justify-between">
        <label className="block text-[12px] font-medium text-hms-textSub">
          {label}
          {required && <span className="text-hms-red ml-0.5">*</span>}
        </label>
        <div className="flex items-center gap-2">
          {ValidationIcon && (
            <span
              className="flex items-center gap-1 text-[10px] text-hms-textDim"
              aria-label={validationLabels[validationType!]}
            >
              <ValidationIcon className="w-3 h-3" />
              <span className="hidden sm:inline">{validationLabels[validationType!]}</span>
            </span>
          )}
          {showCharCount && (
            <span
              className={cn(
                'text-[10px] tabular-nums',
                charCountExceeded ? 'text-hms-red' : 'text-hms-textDim'
              )}
              aria-label={`${currentLength} of ${maxLength} characters`}
            >
              {currentLength}/{maxLength}
            </span>
          )}
        </div>
      </div>
      {children}
      {error && <p className="text-[11px] text-hms-red">{error}</p>}
      {helpText && !error && (
        <p className="text-[11px] text-hms-textDim leading-relaxed">{helpText}</p>
      )}
      {hint && !error && !helpText && (
        <p className="text-[11px] text-hms-textDim">{hint}</p>
      )}
    </div>
  );
}
